import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackingApi } from '../api/services';
import toast from 'react-hot-toast';
import { Share2, Loader2 } from 'lucide-react';

export default function ShareWithOps() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await trackingApi.inTransit();
      setOrders(res.data.orders || []);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function toggle(id) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  async function share() {
    if (selected.size === 0) return;
    setSharing(true);
    try {
      const res = await trackingApi.shareWithOps({ orderIds: [...selected] });
      toast.success(`Shared ${res.data.shared?.length || 0} order(s) with Ops`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Share failed');
    } finally { setSharing(false); }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Share tracking with Ops</h1>
      <p className="text-sm text-gray-500 mb-4">
        Orders in <em>in_transit</em> — pick ones to hand to Ops for customer notification.
        They move to <em>completed</em> once Ops closes.
      </p>

      <div className="mb-3 flex items-center gap-2">
        <button
          onClick={share} disabled={selected.size === 0 || sharing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          Share {selected.size || 0} with Ops
        </button>
        {selected.size > 0 && (
          <button onClick={() => setSelected(new Set())} className="text-sm text-gray-600 underline">
            Clear selection
          </button>
        )}
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}
      {!loading && orders.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-600">No orders currently in transit.</p>
        </div>
      )}
      {!loading && orders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
              <tr>
                <th className="w-8 px-3 py-2">
                  <input type="checkbox"
                         checked={orders.length > 0 && selected.size === orders.length}
                         onChange={(e) => setSelected(e.target.checked ? new Set(orders.map(o => o.id)) : new Set())} />
                </th>
                <th className="text-left px-4 py-2">Order</th>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-left px-4 py-2">Tracking</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
                  </td>
                  <td className="px-4 py-2 font-mono text-blue-600">
                    <Link to={`/orders/${o.id}`}>{o.order_id_external}</Link>
                  </td>
                  <td className="px-4 py-2">{o.customer_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.tracking_numbers || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
